import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LocalSysChartAcctMapService } from './local-sys-chart-acct-map.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { NgReallyClickService } from '../../../shared/components/ng-really-click/ng-really-click.service';

@Component({
    selector: 'gtw-local-sys-chart-acct-map',
    templateUrl: './local-sys-chart-acct-map.component.html',
    styleUrls: ['./local-sys-chart-acct-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LocalSysChartAcctMapComponent implements OnInit, OnDestroy {
  private static readonly bridgeHostSelector = 'gtw-local-sys-chart-acct-map-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private isInitialized = false;
  private isLoading = false;
  private unsubscribe$ = new Subject<void>();
  private loadEpoch = 0;
  private formFetchEpoch = 0;

  // Standard component properties
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Loading states
  loadingSysAcct = true;
  loadingLookup = false;
  loadingTaxSensLookup = true;

  // Form data
  chartAcctData: any = {
    acct_type: '',
    local_chart: '',
    localaccts: '',
    system_acct: '',
    local_acct_tax_sensitive: ''
  };

  // System Account dropdown (string-based, matching FIR)
  sysAcctSelected = 'Please select System Account';
  sysAccDropdownData: string[] = [];
  sysAccDropdownDataTemp: any[] = [];
  sysAcc: any = {};

  // Lookup panel
  lookUpMapping = false;
  showDetails: any = { text: 'lookup' };
  systemAcctLookupTemp: any[] = [];
  lookupForm: string[] = [];
  lookupSchedule: string[] = [];
  lookupSection: string[] = [];
  lookupPart: string[] = [];
  lookupLine: string[] = [];
  lookupColumn: string[] = [];
  lookupSysMapAcct: string[] = [];
  lookupcheckdata: any[] = [];

  CHECK_FORM = '';
  CHECK_SCHEDULE = '';
  CHECK_SECTION = '';
  CHECK_PART = '';
  CHECK_LINE = '';
  CHECK_LINE_DESC = '';
  CHECK_COL = '';
  CHECK_SYS_MAP_ACCT = '';

  // Tax Sensitive panel (non-FIR pattern)
  taxSensLookup = true;
  taxSensitiveLookUpArrow = true;
  loadSystemTaxsensitivitydata: any[] = [];
  templocalaccount: string[] = [];
  templateIdMap: { [display: string]: string } = {};
  irsFormData: any[] = [];

  // Tabs
  activeTab = 'mapping';

  // Audit
  auditData: any[] = [];
  loadingAudit = false;

  // Sorting for TaxSensitive table (8 columns)
  orderByTaxField = 'SCHDM_TEMPLATE';
  reverseTaxSort: boolean | string = false;
  sort_key_tax: any = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, '6': 0, '7': 0, '8': 0 };

  // Sorting for Audit table (8 columns)
  orderByAuditField = 'UPDATED_ON';
  reverseAuditSort: boolean | string = false;
  sort_key_audit: any = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, '6': 0, '7': 0, '8': 0 };

  // Sorting for IRS Form table (7 columns)
  orderByField = 'FORM';
  reverseSort: boolean | string = false;
  sort_key: any = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, '6': 0, '7': 0 };

  // Local accounts handling (single from rowData, not multi-select)
  localAccountKey = '';
  selectedSysAcc = '';
  sysAcctHide = false;

  // Workspace context
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private localSysService: LocalSysChartAcctMapService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private ngReallyService: NgReallyClickService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.formFetchEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = {
      ...gc,
      base_url: gc.base_url || gc.api || '',
      api_url: gc.api_url ?? gc.api ?? '',
    };
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.formFetchEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return String(
      this.GENERAL_CONFIG?.base_url ||
        this.GENERAL_CONFIG?.api_url ||
        this.GENERAL_CONFIG?.api ||
        ''
    );
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.resolveApiBase()) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    const epoch = ++this.loadEpoch;
    try {
      this.setupFormData();
      this.getSystemAcctDropdown(epoch);
      this.fetchSystemAcctLookup(epoch);
      this.fetchTaxSensitiveData(epoch);
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupFormData(): void {
    const source = this.rowData || {};
    this.chartAcctData = {
      acct_type: source.ACCT_TYPE || '',
      local_chart: source.CHART_DISPLAY || '',
      localaccts: source.LOCAL_ACCT_DISPLAY || '',
      system_acct: source.SYSTEM_ACCT || '',
      local_acct_tax_sensitive: source.SENSITIVE_ACCT || ''
    };
    this.selectedSysAcc = source.SYSTEM_ACCT || '';
    this.localAccountKey = source.LOCAL_ACCT_KEY || '';
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  private getTaxYear(): any {
    return this.rowData?.TAX_YEAR ?? this.globalService?.globalParams?.tax_year ?? '';
  }

  private getScenario(): any {
    return this.rowData?.SCENARIO ?? this.globalService?.globalParams?.scenario ?? '';
  }

  private getJcdKey(): any {
    return this.rowData?.JCD_KEY ?? this.globalService?.globalParams?.jcd_key ?? '';
  }

  // System Account Dropdown (action: 9bappg)
  private getSystemAcctDropdown(epoch: number): void {
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }

    const params = {
      action_code: '9bappg',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey()
    };

    this.localSysService.getSystemAcctDropdown(baseUrl, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (epoch !== this.loadEpoch) { return; }
          if (data.callSuccess === '1' && data.jsonObject) {
            this.sysAccDropdownDataTemp = data.jsonObject;
            this.sysAccDropdownData = data.jsonObject.map((item: any) => item.SYS_DROPDOWN);

            const index = this.sysAccDropdownDataTemp.findIndex(
              (item: any) => item.SYS_ACCT === this.selectedSysAcc
            );
            if (index !== -1) {
              this.sysAcc.SYS_ACCT = this.sysAccDropdownDataTemp[index];
              this.sysAcctSelected = this.sysAccDropdownDataTemp[index].SYS_DROPDOWN || 'Please select System Account';
              this.fetchSystemAcctFormData();
            }
          }
          this.loadingSysAcct = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (epoch !== this.loadEpoch) { return; }
          this.loadingSysAcct = false;
          console.error('Error loading system account dropdown:', error);
          this.cdr.detectChanges();
        }
      );
  }

  // Fetch System Account Lookup (action: 5gdshx)
  private fetchSystemAcctLookup(epoch: number): void {
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }

    const params = {
      action_code: '5gdshx',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey()
    };

    this.localSysService.getSystemAcctLookup(baseUrl, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (epoch !== this.loadEpoch) { return; }
          if (data.callSuccess === '1' && data.jsonObject) {
            this.systemAcctLookupTemp = data.jsonObject;
            this.lookupcheckdata = data.jsonObject;
            this.lookupForm = [...new Set(data.jsonObject.map((item: any) => item.FORM))] as string[];
          }
          this.loadingLookup = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (epoch !== this.loadEpoch) { return; }
          this.loadingLookup = false;
          console.error('Error loading lookup data:', error);
          this.cdr.detectChanges();
        }
      );
  }

  // Fetch Tax Sensitive Data (action: 5rvabb, GET request)
  private fetchTaxSensitiveData(epoch: number): void {
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }

    const taxYear = String(this.getTaxYear() ?? '');
    const scenario = String(this.getScenario() ?? '');
    const jcdKey = String(this.getJcdKey() ?? '');
    // Build composite key matching legacy: LOCAL_CHART_KEY_LOCAL_ACCT_KEY-SYS_ACCT_KEY
    // SYS_ACCT_KEY comes from the selected system account (tempDropData in legacy)
    const src = this.rowData || {};
    const sysAcctKey = String(src.SYS_ACCT_KEY || '');
    let sysAcctBase: string;
    if (src.LOCAL_CHART_KEY && src.LOCAL_ACCT_KEY) {
      sysAcctBase = `${src.LOCAL_CHART_KEY}_${src.LOCAL_ACCT_KEY}`;
    } else {
      sysAcctBase = String(src.LOCAL_CHART_ACCT_KEY ?? '');
    }
    const sysAcct = sysAcctKey ? `${sysAcctBase}-${sysAcctKey}` : sysAcctBase;
    console.log('[TaxSens] fetchTaxSensitiveData: sys_acct built as:', sysAcct, '| base:', sysAcctBase, '| sysAcctKey:', sysAcctKey);

    this.localSysService.loadTaxSensitiveData(baseUrl, taxYear, sysAcct, scenario, jcdKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (epoch !== this.loadEpoch) { return; }
          console.log('[TaxSens] fetchTaxSensitiveData raw response:', data);
          if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
            this.loadSystemTaxsensitivitydata = data.jsonObject.map((row: any) => ({
              ...row,
              isEditable: false,
              saveFlag: false,
              oldtemplatevalue: '',
              olduserdescvalue: '',
            }));
            this.templocalaccount = Array.from(
              new Set(
                this.loadSystemTaxsensitivitydata
                  .map((r: any) => r.SCHDM_TEMPLATE)
                  .filter(Boolean)
              )
            );
            // Build display→ID map for cascade API calls.
            // SCHDM_TEMPLATE = unique option per row (dropdown choices).
            // SCHDM_TEMPLATE_DEFAULT = currently selected template for the TRIGGER_ACCT (same across rows of same TRIGGER_ACCT).
            // Map both so changeTemplate and loadEditDropdowns can look up IDs for any selection.
            this.templateIdMap = {};
            this.loadSystemTaxsensitivitydata.forEach((r: any) => {
              if (r.SCHDM_TEMPLATE && r.SCHDM_TEMPLATE_ID) {
                this.templateIdMap[r.SCHDM_TEMPLATE] = r.SCHDM_TEMPLATE_ID;
              }
              if (r.SCHDM_TEMPLATE_DEFAULT && r.SCHDM_TEMPLATE_ID_DEFAULT) {
                this.templateIdMap[r.SCHDM_TEMPLATE_DEFAULT] = r.SCHDM_TEMPLATE_ID_DEFAULT;
              }
            });
            // Do NOT overwrite SCHDM_TEMPLATE — it holds the unique option name per row used for the dropdown.
            // Only normalise SCHDM_TEMPLATE_ID so it is available on every row.
            this.loadSystemTaxsensitivitydata = this.loadSystemTaxsensitivitydata.map((row: any) => ({
              ...row,
              SCHDM_TEMPLATE_ID: row.SCHDM_TEMPLATE_ID || row.SCHDM_TEMPLATE_ID_DEFAULT || this.templateIdMap[row.SCHDM_TEMPLATE] || '',
            }));
            console.log('[TaxSens] loadSystemTaxsensitivitydata count:', this.loadSystemTaxsensitivitydata.length);
            console.log('[TaxSens] templocalaccount:', this.templocalaccount);
            console.log('[TaxSens] templateIdMap:', this.templateIdMap);
            console.log('[TaxSens] first row keys:', this.loadSystemTaxsensitivitydata.length > 0 ? Object.keys(this.loadSystemTaxsensitivitydata[0]) : 'empty');
          } else {
            this.loadSystemTaxsensitivitydata = [];
            console.warn('[TaxSens] fetchTaxSensitiveData: no data or callSuccess != 1');
          }
          this.loadingTaxSensLookup = false;
          this.taxSensitiveLookUpArrow = true;
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (epoch !== this.loadEpoch) { return; }
          this.loadingTaxSensLookup = false;
          this.loadSystemTaxsensitivitydata = [];
          console.error('Error loading tax sensitive data:', error);
          this.cdr.detectChanges();
        }
      );
  }

  // Fetch IRS Form Data (action: 5hsimn, GET request)
  private fetchSystemAcctFormData(): void {
    if (!this.sysAcc?.SYS_ACCT?.SYS_ACCT_KEY) { return; }

    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }

    const formEpoch = ++this.formFetchEpoch;

    const params: any = {
      action_code: '5hsimn',
      tax_year: this.getTaxYear(),
      local_acct: this.localAccountKey,
      sys_acct: this.sysAcc.SYS_ACCT.SYS_ACCT_KEY,
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey()
    };
    const allParams = { ...this.filterParams, ...this.globalService?.globalParams, ...params };

    this.localSysService.getSystemAcctFormData(baseUrl, allParams, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (formEpoch !== this.formFetchEpoch) { return; }
          if (data.callSuccess === '1' && data.jsonObject) {
            this.irsFormData = data.jsonObject;
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          if (formEpoch !== this.formFetchEpoch) { return; }
          console.error('Error loading IRS form data:', error);
          this.cdr.detectChanges();
        }
      );
  }

  // System Account change handler
  onSystemAcctChange(): void {
    const index = this.sysAccDropdownDataTemp.findIndex(
      (item: any) => item.SYS_DROPDOWN === this.sysAcctSelected
    );
    if (index !== -1) {
      this.sysAcc.SYS_ACCT = this.sysAccDropdownDataTemp[index];
      this.chartAcctData.system_acct = this.sysAccDropdownDataTemp[index].SYS_ACCT;

      if (this.sysAcc.SYS_ACCT.TAX_SENSITIVE === 'Y') {
        this._snackBar.open('Selected system account is tax sensitive', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
      }

      // Build composite sys_acct key for tax sensitive data: LOCAL_CHART_KEY_LOCAL_ACCT_KEY-SYS_ACCT_KEY
      const localChartKey = String(this.rowData?.LOCAL_CHART_KEY ?? '');
      const localAcctKey = String(this.rowData?.LOCAL_ACCT_KEY ?? '');
      const sysAcctKey = String(this.sysAccDropdownDataTemp[index].SYS_ACCT_KEY ?? '');
      this.rowData.LOCAL_CHART_ACCT_KEY = localChartKey + '_' + localAcctKey + '-' + sysAcctKey;

      this.fetchSystemAcctFormData();
      this.fetchTaxSensitiveData(this.loadEpoch);
    }
    this.cdr.detectChanges();
  }

  // Toggle lookup panel
  toggleLookup(): void {
    this.CHECK_FORM = '';
    this.CHECK_SCHEDULE = '';
    this.CHECK_SECTION = '';
    this.CHECK_PART = '';
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    this.lookUpMapping = !this.lookUpMapping;
    this.showDetails.text = 'lookup';
    this.cdr.detectChanges();
  }

  // Toggle Tax Sensitive panel
  taxSensitiveToggleLookup(): void {
    this.taxSensitiveLookUpArrow = !this.taxSensitiveLookUpArrow;
    this.cdr.detectChanges();
  }

  // Cascade lookup selection (7-level: Form→Schedule→Section→Part→Line→Column→SysMapAcct)
  getLookupNext(key: number, nextLookupKey: string, currentLookupValue: string): void {
    const lookupData = [...this.lookupcheckdata];

    switch (key) {
      case 1:
        this.lookupSchedule = this.filterLookupData(lookupData, 'FORM', currentLookupValue, 'SCHEDULE');
        break;
      case 2: {
        const scheduleFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FORM && obj.SCHEDULE === currentLookupValue
        );
        this.lookupSection = [...new Set(scheduleFiltered.map((obj: any) => obj.FORM_SECTION_NAME))];
        break;
      }
      case 3: {
        const sectionFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FORM &&
          obj.SCHEDULE === this.CHECK_SCHEDULE &&
          obj.FORM_SECTION_NAME === currentLookupValue
        );
        this.lookupPart = [...new Set(sectionFiltered.map((obj: any) => obj.FORM_PART_NO))];
        break;
      }
      case 4: {
        const partFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FORM &&
          obj.SCHEDULE === this.CHECK_SCHEDULE &&
          obj.FORM_SECTION_NAME === this.CHECK_SECTION &&
          obj.FORM_PART_NO === currentLookupValue
        );
        this.lookupLine = [...new Set(partFiltered.map((obj: any) => obj.LINE))];
        break;
      }
      case 5: {
        const lineFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FORM &&
          obj.SCHEDULE === this.CHECK_SCHEDULE &&
          obj.FORM_SECTION_NAME === this.CHECK_SECTION &&
          obj.FORM_PART_NO === this.CHECK_PART &&
          obj.LINE === currentLookupValue
        );
        this.lookupColumn = [...new Set(lineFiltered.map((obj: any) => obj.COL))];
        break;
      }
      case 6: {
        const colFiltered = lookupData.filter((obj: any) =>
          obj.FORM === this.CHECK_FORM &&
          obj.SCHEDULE === this.CHECK_SCHEDULE &&
          obj.FORM_SECTION_NAME === this.CHECK_SECTION &&
          obj.FORM_PART_NO === this.CHECK_PART &&
          obj.LINE === this.CHECK_LINE &&
          obj.COL === currentLookupValue
        );
        this.lookupSysMapAcct = [...new Set(colFiltered.map((obj: any) => obj.SYS_ACCT))];
        break;
      }
    }
    this.cdr.detectChanges();
  }

  private filterLookupData(data: any[], filterKey: string, filterValue: string, extractKey: string): string[] {
    const filtered = data.filter((obj: any) => obj[filterKey] === filterValue);
    return [...new Set(filtered.map((obj: any) => obj[extractKey]))];
  }

  // Tax Sensitive edit (non-FIR pattern)
  formEdit(row: any): void {
    // Build options FIRST (before mutating row) so the default selection can be validated against the list.
    // Use SCHDM_TEMPLATE — each row holds the unique option name for that row (not SCHDM_TEMPLATE_DEFAULT).
    this.templocalaccount = [...new Set(
      this.loadSystemTaxsensitivitydata
        .filter((r: any) => r.TRIGGER_ACCT === row.TRIGGER_ACCT)
        .map((r: any) => r.SCHDM_TEMPLATE)
        .filter(Boolean)
    )];

    row.isEditable = true;
    row.saveFlag = true;

    // Pre-select the currently assigned template (SCHDM_TEMPLATE_DEFAULT).
    // Only use it if it's actually present in the options list; otherwise leave null
    // so the dropdown shows its placeholder and the user must pick a template.
    const assignedTemplate = row.SCHDM_TEMPLATE_DEFAULT || null;
    row.dropdowndata = (assignedTemplate && this.templocalaccount.includes(assignedTemplate))
      ? assignedTemplate
      : null;

    // Save original values for cancel/reset
    row.oldtemplatevalue = row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE;
    row.SCHDM_TEMPLATE_ID = row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || this.templateIdMap[row.SCHDM_TEMPLATE_DEFAULT] || '';
    row.olduserdescvalue = row.USER_DESC;
    row.oldscdhmtypevalue = row.SCHDM_TYPE_DEFAULT_SAVE ?? row.SCHDM_TYPE;
    row.oldmethodofcalcvalue = row.METHOD_OF_CALC_DEFAULT_SAVE ?? row.METHOD_OF_CALC;
    row.oldpostingtacvalue = row.POSTING_TAC_DEFAULT_SAVE ?? row.POSTING_TAC;
    row.oldpostingacctvalue = row.POSTING_ACCT_DEFAULT_SAVE ?? row.POSTING_ACCT;
    row.oldtbbsacctvalue = row.TAX_STREAM_ACCT_DEFAULT_SAVE ?? row.TAX_STREAM_ACCT;
    row.oldmultipliervalue = row.MULTIPLIER;

    this.loadEditDropdowns(row);
    this.cdr.detectChanges();
  }

  private loadEditDropdowns(row: any): void {
    console.log('[TaxSens] loadEditDropdowns called for row:', row.SCHDM_TEMPLATE);
    row.schdmtypedropdown = [];
    row.schdmtypedropdown_all = [];
    row.methodofcaldropdown = [];
    row.methodofcaldropdown_all = [];
    row.postingtacdropdown = [];
    row.postingtacdropdown_all = [];
    row.postingacctdropdown = [];
    row.postingacctdropdown_all = [];
    row.tbbsacctdropdown = [];
    row.tbbsacctdropdown_all = [];

    const baseUrl = this.resolveApiBase();
    console.log('[TaxSens] loadEditDropdowns: baseUrl:', baseUrl);
    if (!baseUrl) { console.warn('[TaxSens] loadEditDropdowns: NO baseUrl!'); return; }

    const source = this.rowData || {};
    const compositeKey = `${source.LOCAL_CHART_KEY}_${source.LOCAL_ACCT_KEY}-${row.SYS_ACCT_KEY || source.SYS_ACCT_KEY || ''}`;
    const templateDisplay = row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE;
    const templateIdDefault = row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || this.templateIdMap[templateDisplay] || '';

    // Step 1: kbaflc — loads SCHDM_TYPE + METHOD_OF_CALC
    const kbaflcParams = {
      action_code: 'kbaflc',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey(),
      sys_acct: compositeKey,
      schdm_temp: templateIdDefault
    };

    console.log('[TaxSens] kbaflc request params:', kbaflcParams);
    this.localSysService.getTemplateData(baseUrl, kbaflcParams, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        console.log('[TaxSens] kbaflc response:', data);
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          const items = data.jsonObject;
          console.log('[TaxSens] kbaflc items count:', items.length, 'first item:', items[0]);
          // SCHDM_TYPE dropdown
          row.schdmtypedropdown_all = items.map((i: any) => ({
            display: i.SCHDM_TYPE || '', save: i.SCHDM_TYPE_SAVE || i.SCHDM_TYPE || ''
          }));
          row.schdmtypedropdown = [...new Set(row.schdmtypedropdown_all.map((i: any) => i.display))];
          // METHOD_OF_CALC dropdown
          row.methodofcaldropdown_all = items.map((i: any) => ({
            display: i.METHOD_OF_CALC || '', save: i.METHOD_OF_CALC_SAVE || i.METHOD_OF_CALC || ''
          }));
          row.methodofcaldropdown = [...new Set(row.methodofcaldropdown_all.map((i: any) => i.display))];
          console.log('[TaxSens] kbaflc: schdmtypedropdown:', row.schdmtypedropdown);
          console.log('[TaxSens] kbaflc: methodofcaldropdown:', row.methodofcaldropdown);
        } else {
          console.warn('[TaxSens] kbaflc: callSuccess != 1 or no jsonObject');
        }
        this.cdr.detectChanges();

        // Step 2: iemg45 — loads POSTING_TAC (depends on kbaflc for template context)
        this.loadPostingTacDropdown(row, baseUrl, compositeKey, templateIdDefault);
        // Step 2b: wfk6zt — loads TBBS (needs schdm_type/schdm_method from kbaflc)
        this.loadTBBSDropdown(row, baseUrl, compositeKey, templateIdDefault);
      }, (err: any) => {
        console.error('[TaxSens] kbaflc ERROR:', err);
      });
  }

  private loadPostingTacDropdown(row: any, baseUrl: string, compositeKey: string, templateId: string): void {
    // ACCT_TYPES 5/6/7 override: sys_acct_de uses SYS_ACCT_KEY when Reverse Current Year Balance
    const postingTacSave = row._overridePostingTacSave != null
      ? row._overridePostingTacSave
      : (row.POSTING_TAC_DEFAULT_SAVE || row.POSTING_TAC || '');
    const iemg45Params = {
      action_code: 'iemg45',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey(),
      sys_acct: compositeKey,
      sys_acct_de: postingTacSave,
      template: templateId
    };

    console.log('[TaxSens] iemg45 request params:', iemg45Params);
    this.localSysService.getPostingTacData(baseUrl, iemg45Params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        console.log('[TaxSens] iemg45 response:', data);
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          row.postingtacdropdown_all = data.jsonObject.map((i: any) => ({
            display: i.POSTING_TAC || '', save: i.POSTING_TAC_SAVE || i.POSTING_TAC || ''
          }));
          row.postingtacdropdown = [...new Set(row.postingtacdropdown_all.map((i: any) => i.display))];
          console.log('[TaxSens] iemg45: postingtacdropdown:', row.postingtacdropdown);
        }
        this.cdr.detectChanges();

        // Step 3: sdcmbv — loads POSTING_ACCT (depends on POSTING_TAC)
        this.loadPostingAcctDropdown(row, baseUrl);
      }, (err: any) => {
        console.error('[TaxSens] iemg45 ERROR:', err);
      });
  }

  private loadPostingAcctDropdown(row: any, baseUrl: string): void {
    const source = this.rowData || {};
    const postingTacSave = this.resolveDropdownSaveKey(row.POSTING_TAC_DEFAULT, row.postingtacdropdown_all) || row.POSTING_TAC || '';
    // Resolve pa_acct save key from display value via postingacctdropdown_all (matching legacy lookup logic)
    const postingAcctSave = row._overridePostingAcctSave != null
      ? row._overridePostingAcctSave
      : (this.resolveDropdownSaveKey(row.POSTING_ACCT_DEFAULT, row.postingacctdropdown_all) || row.POSTING_ACCT_DEFAULT_SAVE || row.POSTING_ACCT || '');

    const sdcmbvParams = {
      action_code: 'sdcmbv',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey(),
      sys_acct: postingTacSave,
      pa_acct: postingAcctSave,
      chart_key: source.LOCAL_CHART_KEY || ''
    };

    console.log('[TaxSens] sdcmbv request params:', sdcmbvParams);
    this.localSysService.getPostingAcctData(baseUrl, sdcmbvParams, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        console.log('[TaxSens] sdcmbv response:', data);
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          row.postingacctdropdown_all = data.jsonObject.map((i: any) => ({
            display: i.POSTING_ACCT || '', save: i.POSTING_ACCT_SAVE || i.POSTING_ACCT || ''
          }));
          row.postingacctdropdown = [...new Set(row.postingacctdropdown_all.map((i: any) => i.display))];
          // Auto-select first element (doc §7.4)
          if (row.postingacctdropdown.length > 0) {
            row.POSTING_ACCT_DEFAULT = row.postingacctdropdown[0];
          }
          console.log('[TaxSens] sdcmbv: postingacctdropdown:', row.postingacctdropdown);
        }
        this.cdr.detectChanges();
      }, (err: any) => {
        console.error('[TaxSens] sdcmbv ERROR:', err);
      });
  }

  private loadTBBSDropdown(row: any, baseUrl: string, compositeKey: string, templateId: string): void {
    const source = this.rowData || {};
    const wfk6ztParams = {
      action_code: 'wfk6zt',
      tax_year: this.getTaxYear(),
      sys_acct: compositeKey,
      local_accts: row.SYS_ACCT_KEY || source.SYS_ACCT_KEY || '',
      schdm_temp: templateId,
      schdm_type: this.resolveDropdownSaveKey(row.SCHDM_TYPE_DEFAULT, row.schdmtypedropdown_all) || row.SCHDM_TYPE_DEFAULT_SAVE || row.SCHDM_TYPE || '',
      schdm_method: this.resolveDropdownSaveKey(row.METHOD_OF_CALC_DEFAULT, row.methodofcaldropdown_all) || row.METHOD_OF_CALC_DEFAULT_SAVE || row.METHOD_OF_CALC || '',
      source_system: source.SOURCE_SYSTEM || ''
    };

    console.log('[TaxSens] wfk6zt request params:', wfk6ztParams);
    this.localSysService.getTBBSAcctData(baseUrl, wfk6ztParams, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        console.log('[TaxSens] wfk6zt response:', data);
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          row.tbbsacctdropdown_all = data.jsonObject.map((i: any) => ({
            display: i.TAX_STREAM_ACCT || '', save: i.TAX_STREAM_ACCT_SAVE || i.TAX_STREAM_ACCT || ''
          }));
          row.tbbsacctdropdown = [...new Set(row.tbbsacctdropdown_all.map((i: any) => i.display))];
          // Auto-select first element (doc §7.5)
          if (row.tbbsacctdropdown.length > 0) {
            row.TAX_STREAM_ACCT_DEFAULT = row.tbbsacctdropdown[0];
          }
          console.log('[TaxSens] wfk6zt: tbbsacctdropdown:', row.tbbsacctdropdown);
        }
        this.cdr.detectChanges();
      }, (err: any) => {
        console.error('[TaxSens] wfk6zt ERROR:', err);
      });
  }

  private resolveDropdownSaveKey(displayValue: string, allItems: any[]): string {
    if (!displayValue || !allItems?.length) { return ''; }
    const found = allItems.find((i: any) => i.display === displayValue);
    return found?.save || displayValue;
  }

  private coerceOptionValue(value: unknown): string | undefined {
    if (value == null) {
      return undefined;
    }
    return typeof value === 'string' ? value : String(value);
  }

  changeTemplate(row: any, selectedTemplate?: unknown): void {
    const selected = this.coerceOptionValue(selectedTemplate);
    if (selected != null) {
      row.dropdowndata = selected;
    }
    row.updated = true;
    // Reload all dropdowns when template changes
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }
    const source = this.rowData || {};
    const compositeKey = `${source.LOCAL_CHART_KEY}_${source.LOCAL_ACCT_KEY}-${row.SYS_ACCT_KEY || source.SYS_ACCT_KEY || ''}`;
    const templateId = this.templateIdMap[row.dropdowndata] || this.templateIdMap[row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE] || row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || '';
    // Reset all dependent dropdowns
    row.schdmtypedropdown = [];
    row.methodofcaldropdown = [];
    row.postingtacdropdown = [];
    row.postingacctdropdown = [];
    row.tbbsacctdropdown = [];
    this.cdr.detectChanges();
    // Reload from kbaflc chain
    const kbaflcParams = {
      action_code: 'kbaflc',
      tax_year: this.getTaxYear(),
      scenario: this.getScenario(),
      jcd_key: this.getJcdKey(),
      sys_acct: compositeKey,
      schdm_temp: templateId
    };
    this.localSysService.getTemplateData(baseUrl, kbaflcParams, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          const items = data.jsonObject;
          row.schdmtypedropdown_all = items.map((i: any) => ({
            display: i.SCHDM_TYPE || '', save: i.SCHDM_TYPE_SAVE || i.SCHDM_TYPE || ''
          }));
          row.schdmtypedropdown = [...new Set(row.schdmtypedropdown_all.map((i: any) => i.display))];
          row.methodofcaldropdown_all = items.map((i: any) => ({
            display: i.METHOD_OF_CALC || '', save: i.METHOD_OF_CALC_SAVE || i.METHOD_OF_CALC || ''
          }));
          row.methodofcaldropdown = [...new Set(row.methodofcaldropdown_all.map((i: any) => i.display))];
          // Apply display defaults from kbaflc response.
          // Legacy matches by SCHDM_TEMPLATE == selected template name (not always items[0]).
          const selectedTemplateName = row.dropdowndata || row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE;
          const matchedItem = items.find((i: any) => i.SCHDM_TEMPLATE === selectedTemplateName) || items[0];
          if (matchedItem) {
            row.USER_DESC = matchedItem.USER_DESC ?? row.USER_DESC;
            row.MULTIPLIER = matchedItem.MULTIPLIER ?? row.MULTIPLIER;
            row.SCHDM_TYPE_DEFAULT = matchedItem.SCHDM_TYPE_DEFAULT || matchedItem.SCHDM_TYPE || row.schdmtypedropdown[0] || '';
            row.METHOD_OF_CALC_DEFAULT = matchedItem.METHOD_OF_CALC_DEFAULT || matchedItem.METHOD_OF_CALC_DESC || matchedItem.METHOD_OF_CALC || row.methodofcaldropdown[0] || '';

            // ACCT_TYPES 5/6/7 (P&L accounts): when METHOD_OF_CALC is 'Reverse Current Year Balance',
            // Posting TAC and Acct are overridden to use the current sys/local acct key (legacy rule)
            const acctTypes = this.rowData?.ACCT_TYPES;
            const isReverseCurrentYear = row.METHOD_OF_CALC_DEFAULT === 'Reverse Current Year Balance';
            if ((acctTypes === '5' || acctTypes === '6' || acctTypes === '7') && isReverseCurrentYear) {
              row.POSTING_TAC_DEFAULT = this.rowData?.SYSTEM_ACCT
                ? `${this.rowData.SYSTEM_ACCT} - ${this.rowData.SYSTEM_ACCT_DESC || ''}`
                : (matchedItem.POSTING_TAC_DEFAULT ?? '');
              row.POSTING_ACCT_DEFAULT = row.TRIGGER_ACCT_DISPLAY || (matchedItem.POSTING_ACCT_DEFAULT ?? '');
              // iemg45 sys_acct_de must use SYS_ACCT_KEY, pa_acct must use LOCAL_ACCT_KEY
              row._overridePostingTacSave = this.rowData?.SYS_ACCT_KEY || '';
              row._overridePostingAcctSave = this.rowData?.LOCAL_ACCT_KEY || '';
            } else {
              row.POSTING_TAC_DEFAULT = matchedItem.POSTING_TAC_DEFAULT ?? '';
              row.POSTING_ACCT_DEFAULT = matchedItem.POSTING_ACCT_DEFAULT ?? '';
              row._overridePostingTacSave = null;
              row._overridePostingAcctSave = null;
            }

            row.TAX_STREAM_ACCT_DEFAULT = matchedItem.TAX_STREAM_ACCT_DEFAULT ?? '';
            row.SCHDM_TEMPLATE_ID = matchedItem.SCHDM_TEMPLATE_ID ?? row.SCHDM_TEMPLATE_ID;
            row.PREFERED_Y_N = matchedItem.PREFERED_Y_N ?? row.PREFERED_Y_N;
          } else {
            if (row.schdmtypedropdown.length) { row.SCHDM_TYPE_DEFAULT = row.schdmtypedropdown[0]; }
            if (row.methodofcaldropdown.length) { row.METHOD_OF_CALC_DEFAULT = row.methodofcaldropdown[0]; }
          }
          row.dispalyDefaultonChange = true;
        }
        this.cdr.detectChanges();
        this.loadPostingTacDropdown(row, baseUrl, compositeKey, templateId);
        // TBBS must load AFTER kbaflc (needs schdm_type/schdm_method)
        this.loadTBBSDropdown(row, baseUrl, compositeKey, templateId);
      }, (err: any) => {
        console.error('[TaxSens] changeTemplate kbaflc ERROR:', err);
      });
  }

  // Cascade: SCHDM_TYPE or METHOD_OF_CALC changed → reload TBBS
  schdmTypeChange(row: any, selectedValue?: unknown): void {
    const selected = this.coerceOptionValue(selectedValue);
    if (selected != null) {
      row.SCHDM_TYPE_DEFAULT = selected;
    }
    row.updated = true;
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }
    const source = this.rowData || {};
    const compositeKey = `${source.LOCAL_CHART_KEY}_${source.LOCAL_ACCT_KEY}-${row.SYS_ACCT_KEY || source.SYS_ACCT_KEY || ''}`;
    const templateId = this.templateIdMap[row.dropdowndata] || this.templateIdMap[row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE] || row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || '';
    this.loadTBBSDropdown(row, baseUrl, compositeKey, templateId);
  }

  methodOfCalcChange(row: any, selectedValue?: unknown): void {
    const selected = this.coerceOptionValue(selectedValue);
    if (selected != null) {
      row.METHOD_OF_CALC_DEFAULT = selected;
    }
    row.updated = true;
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }
    const source = this.rowData || {};
    const compositeKey = `${source.LOCAL_CHART_KEY}_${source.LOCAL_ACCT_KEY}-${row.SYS_ACCT_KEY || source.SYS_ACCT_KEY || ''}`;
    const templateId = this.templateIdMap[row.dropdowndata] || this.templateIdMap[row.SCHDM_TEMPLATE_DEFAULT || row.SCHDM_TEMPLATE] || row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || '';
    this.loadTBBSDropdown(row, baseUrl, compositeKey, templateId);
  }

  // Cascade: POSTING_TAC changed → reload POSTING_ACCT
  postingTacChange(row: any, selectedValue?: unknown): void {
    const selected = this.coerceOptionValue(selectedValue);
    if (selected != null) {
      row.POSTING_TAC_DEFAULT = selected;
    }
    row.updated = true;
    const baseUrl = this.resolveApiBase();
    if (!baseUrl) { return; }
    row.postingacctdropdown = [];
    this.cdr.detectChanges();
    this.loadPostingAcctDropdown(row, baseUrl);
  }

  postingAcctChange(row: any, selectedValue?: unknown): void {
    const selected = this.coerceOptionValue(selectedValue);
    if (selected != null) {
      row.POSTING_ACCT_DEFAULT = selected;
    }
    row.updated = true;
    this.cdr.detectChanges();
  }

  tbbsAcctChange(row: any, selectedValue?: unknown): void {
    const selected = this.coerceOptionValue(selectedValue);
    if (selected != null) {
      row.TAX_STREAM_ACCT_DEFAULT = selected;
    }
    row.updated = true;
    this.cdr.detectChanges();
  }

  formDelete(row: any): void {
    this.ngReallyService.openNgReallyDialog(
      'Please Confirm',
      'Are you sure you want to delete the System and Local Account?'
    ).afterClosed().pipe(takeUntil(this.unsubscribe$)).subscribe((result: any) => {
      if (!result) { return; }
      // Delete entire TRIGGER_ACCT group (doc §10.4)
      this.loadSystemTaxsensitivitydata = this.loadSystemTaxsensitivitydata.filter(
        (r: any) => r.TRIGGER_ACCT !== row.TRIGGER_ACCT
      );
      const deleteObj = this.buildDeleteSettings(row);
      const details = [deleteObj];
      // jsonSettings must be the local-to-system account array (type:'E') — mirrors legacy sendDeleteDetails call
      const localSystemAccounts = this.buildLocalSystemAccounts();
      this.localSysService.deleteTaxSensitiveDetails(
        this.resolveApiBase(),
        details,
        localSystemAccounts,
        this.globalService,
        this.filterParams
      ).pipe(takeUntil(this.unsubscribe$)).subscribe(
        (deleteResult: any) => {
          if (deleteResult?.errorMessage && deleteResult.errorMessage !== 'null') {
            this._snackBar.open(deleteResult.errorMessage, 'Close', {
              duration: 4000, panelClass: ['error-snackbar']
            });
            return;
          }
          this._snackBar.open('Data has been deleted successfully', 'Close', {
            duration: 4000, panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        },
        () => {
          this._snackBar.open('Error occurred while deleting.', 'Close', {
            duration: 4000, panelClass: ['error-snackbar']
          });
        }
      );
      this.cdr.detectChanges();
    });
  }

  get sortedRows(): any[] {
    // Match legacy behavior: preserve backend priority per TRIGGER_ACCT before unique-filtering.
    const prioritizedRows = [...this.loadSystemTaxsensitivitydata].sort((a: any, b: any) => {
      const acctCompare = String(a?.TRIGGER_ACCT ?? '').localeCompare(String(b?.TRIGGER_ACCT ?? ''));
      if (acctCompare !== 0) {
        return acctCompare;
      }

      const aOrder = Number(a?.P_ORDER ?? Number.MAX_SAFE_INTEGER);
      const bOrder = Number(b?.P_ORDER ?? Number.MAX_SAFE_INTEGER);
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return String(a?.SCHDM_TEMPLATE ?? '').localeCompare(String(b?.SCHDM_TEMPLATE ?? ''));
    });

    const seen = new Set<string>();
    const uniqueRows = prioritizedRows.filter((row: any) => {
      const key = row.TRIGGER_ACCT ?? '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const rows = [...uniqueRows];
    if (!this.orderByTaxField) { return rows; }
    rows.sort((a: any, b: any) => {
      const av = String(a?.[this.orderByTaxField] ?? '');
      const bv = String(b?.[this.orderByTaxField] ?? '');
      return av.localeCompare(bv);
    });
    if (this.reverseTaxSort === true) { rows.reverse(); }
    return rows;
  }

  sortByTax(key: string, currentValue: number): void {
    const fieldMap: any = {
      '1': 'SCHDM_TEMPLATE', '2': 'USER_DESC', '3': 'SCHDM_TYPE',
      '4': 'METHOD_OF_CALC', '5': 'MULTIPLIER', '6': 'POSTING_TAC',
      '7': 'POSTING_ACCT', '8': 'TAX_STREAM_ACCT'
    };
    let newValue: number;
    switch (currentValue) {
      case 0: newValue = 1; this.reverseTaxSort = false; break;
      case 1: newValue = 2; this.reverseTaxSort = true; break;
      case 2: default: newValue = 0; this.reverseTaxSort = ''; this.orderByTaxField = ''; break;
    }
    Object.keys(this.sort_key_tax).forEach(k => this.sort_key_tax[k] = 0);
    this.sort_key_tax[key] = newValue;
    if (newValue !== 0) {
      this.orderByTaxField = fieldMap[key] || 'SCHDM_TEMPLATE';
    }
    this.cdr.detectChanges();
  }

  sortByAudit(key: string, currentValue: number): void {
    let newValue: number;
    switch (currentValue) {
      case 0: newValue = 1; this.reverseAuditSort = false; break;
      case 1: newValue = 2; this.reverseAuditSort = true; break;
      case 2: default: newValue = 0; this.reverseAuditSort = ''; this.orderByAuditField = ''; break;
    }
    Object.keys(this.sort_key_audit).forEach(k => this.sort_key_audit[k] = 0);
    this.sort_key_audit[key] = newValue;

    if (newValue !== 0 && this.auditData.length > 0) {
      const fieldMap: any = {
        '1': 'UPDATED_ON', '2': 'LOCAL_CHART', '3': 'SYSTEM_CHART', '4': 'LOCAL_ACCT',
        '5': 'SYSTEM_ACCT', '6': 'DML_TYPE', '7': 'OLD_NEW_FLAG', '8': 'UPDATED_BY'
      };
      const sortField = fieldMap[key] || 'UPDATED_ON';
      this.auditData = [...this.auditData].sort((a, b) => {
        const valA = (a[sortField] || '').toString().toLowerCase();
        const valB = (b[sortField] || '').toString().toLowerCase();
        const comparison = valA.localeCompare(valB);
        return this.reverseAuditSort === true ? -comparison : comparison;
      });
    }
    this.cdr.detectChanges();
  }

  private buildSaveDetail(row: any): any {
    const source = this.rowData || {};
    const newSchdmType = this.resolveDropdownSaveKey(row.SCHDM_TYPE_DEFAULT, row.schdmtypedropdown_all) || row.SCHDM_TYPE || '';
    const newMethodOfCalc = this.resolveDropdownSaveKey(row.METHOD_OF_CALC_DEFAULT, row.methodofcaldropdown_all) || row.METHOD_OF_CALC || '';
    const newPostingTac = this.resolveDropdownSaveKey(row.POSTING_TAC_DEFAULT, row.postingtacdropdown_all) || row.POSTING_TAC || '';
    const newPostingAcct = this.resolveDropdownSaveKey(row.POSTING_ACCT_DEFAULT, row.postingacctdropdown_all) || row.POSTING_ACCT || '';
    const newTbbsAcct = this.resolveDropdownSaveKey(row.TAX_STREAM_ACCT_DEFAULT, row.tbbsacctdropdown_all) || row.TAX_STREAM_ACCT || '';

    return {
      type: 'E',
      object_id: source.object_id,
      tax_year: source.TAX_YEAR,
      scenario: this.getScenario(),
      triggerchart: row.TRIGGER_CHART,
      triggeracct: row.TRIGGER_ACCT,
      newtemplatevalue: row.dropdowndata ?? row.oldtemplatevalue,
      oldtemplatevalue: row.oldtemplatevalue,
      newtemplateid: this.templateIdMap[row.dropdowndata] || row.SCHDM_TEMPLATE_ID_DEFAULT || row.SCHDM_TEMPLATE_ID || '',
      olduserdescvalue: row.olduserdescvalue,
      newuserdescvalue: row.USER_DESC,
      oldschdmtype: row.oldscdhmtypevalue || '',
      newschdmtype: newSchdmType,
      oldmethodofcalc: row.oldmethodofcalcvalue || '',
      newmethodofcalc: newMethodOfCalc,
      oldmultipliervalue: row.oldmultipliervalue || '',
      newmultipliervalue: row.MULTIPLIER || '',
      oldpostingtac: row.oldpostingtacvalue || '',
      newpostingtac: newPostingTac,
      postingtac: newPostingTac,
      oldpostingacct: row.oldpostingacctvalue || '',
      newpostingacct: newPostingAcct,
      postingacct: newPostingAcct,
      oldtbbsacct: row.oldtbbsacctvalue || '',
      newtbbsacct: newTbbsAcct,
      scdhmid: row.SCHDM_ID,
      prefered_y_n: row.PREFERED_Y_N || 'N',
    };
  }

  private buildDeleteSettings(row: any): any {
    const source = this.rowData || {};
    // Field names must match legacy exactly (triggeracct not trigger_acct; no posting_tac/posting_acct)
    return {
      type: 'D',
      object_id: source.object_id,
      tax_year: source.TAX_YEAR,
      scenario: String(this.getScenario()),
      triggeracct: row.TRIGGER_ACCT,
      scdhmid: row.SCHDM_ID,
      triggerchart: row.TRIGGER_CHART,
    };
  }

  // Build the local-to-system account array (type:'E') used as jsonSettings for delete and save
  private buildLocalSystemAccounts(): any[] {
    const source = this.rowData || {};
    const localAcctDisplay: string = this.chartAcctData?.localaccts || source.LOCAL_ACCT_DISPLAY || '';
    const lines = localAcctDisplay ? localAcctDisplay.split('\n') : [''];
    return lines.map((item: string, index: number) => ({
      type: 'E',
      object_id: source.object_id,
      system_acct_key_old: source.SYS_ACCT_KEY,
      system_acct_key_new: this.sysAcc?.SYS_ACCT?.SYS_ACCT_KEY || source.SYS_ACCT_KEY,
      local_acct_key: source.LOCAL_ACCT_KEY,
      map_type: source.MAP_TYPE,
      group_object_key: source.GROUP_OBJ_KEY,
      tax_year: source.TAX_YEAR,
      local_chart_key: source.LOCAL_CHART_KEY,
      scenario: String(this.getScenario()),
      local_account: item,
      system_account: this.sysAcctSelected,
    }));
  }

  // Sort function for IRS Form table
  sortBy(key: string, currentValue: number): void {
    let newValue: number;
    switch (currentValue) {
      case 0:
        newValue = 1;
        this.reverseSort = false;
        break;
      case 1:
        newValue = 2;
        this.reverseSort = true;
        break;
      case 2:
      default:
        newValue = 0;
        this.reverseSort = '';
        break;
    }
    Object.keys(this.sort_key).forEach(k => this.sort_key[k] = 0);
    this.sort_key[key] = newValue;

    if (newValue !== 0 && this.irsFormData.length > 0) {
      const sortField = this.getSortField(key);
      this.irsFormData = [...this.irsFormData].sort((a, b) => {
        const valA = (a[sortField] || '').toString().toLowerCase();
        const valB = (b[sortField] || '').toString().toLowerCase();
        const comparison = valA.localeCompare(valB);
        return this.reverseSort === true ? -comparison : comparison;
      });
    }
    this.cdr.detectChanges();
  }

  private getSortField(key: string): string {
    const fieldMap: any = {
      '1': 'FORM',
      '2': 'SCHEDULE',
      '3': 'FORM_SECTION_NAME',
      '4': 'FORM_PART_NO',
      '5': 'LINE',
      '6': 'LINE_DESC',
      '7': 'COL'
    };
    return fieldMap[key] || 'FORM';
  }

  // Apply lookup changes
  applyChanges(): void {
    if (!this.CHECK_SYS_MAP_ACCT) {
      this._snackBar.open('Please select a system account from lookup', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    const index = this.sysAccDropdownDataTemp.findIndex(
      (item: any) => item.SYS_ACCT === this.CHECK_SYS_MAP_ACCT
    );
    if (index !== -1) {
      this.sysAcctSelected = this.sysAccDropdownDataTemp[index].SYS_DROPDOWN;
      this.sysAcc.SYS_ACCT = this.sysAccDropdownDataTemp[index];
      this.chartAcctData.system_acct = this.sysAccDropdownDataTemp[index].SYS_ACCT;

      if (this.sysAcc.SYS_ACCT.TAX_SENSITIVE === 'Y') {
        this._snackBar.open('Selected system account is tax sensitive', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
      }
    }

    this.fetchSystemAcctFormData();
    this.lookUpMapping = false;
    this.cdr.detectChanges();
  }

  // Reset lookup
  resetLookup(): void {
    this.CHECK_FORM = '';
    this.CHECK_SCHEDULE = '';
    this.CHECK_SECTION = '';
    this.CHECK_PART = '';
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    this.lookupSchedule = [];
    this.lookupSection = [];
    this.lookupPart = [];
    this.lookupLine = [];
    this.lookupColumn = [];
    this.lookupSysMapAcct = [];
    this.cdr.detectChanges();
  }

  // Cascade reset methods (7-level) with auto-selection when only 1 option
  formChanged(): void {
    this.CHECK_SCHEDULE = '';
    this.CHECK_SECTION = '';
    this.CHECK_PART = '';
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupSchedule.length === 1) {
      this.CHECK_SCHEDULE = this.lookupSchedule[0];
      this.getLookupNext(2, 'FORM_SECTION_NAME', this.CHECK_SCHEDULE);
      this.scheduleChanged();
    }
  }

  scheduleChanged(): void {
    this.CHECK_SECTION = '';
    this.CHECK_PART = '';
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupSection.length === 1) {
      this.CHECK_SECTION = this.lookupSection[0];
      this.getLookupNext(3, 'FORM_PART_NO', this.CHECK_SECTION);
      this.sectionChanged();
    }
  }

  sectionChanged(): void {
    this.CHECK_PART = '';
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupPart.length === 1) {
      this.CHECK_PART = this.lookupPart[0];
      this.getLookupNext(4, 'LINE', this.CHECK_PART);
      this.partChanged();
    }
  }

  partChanged(): void {
    this.CHECK_LINE = '';
    this.CHECK_LINE_DESC = '';
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupLine.length === 1) {
      this.CHECK_LINE = this.lookupLine[0];
      this.getLookupNext(5, 'COL', this.CHECK_LINE);
      this.lineChanged();
    }
  }

  lineChanged(): void {
    this.CHECK_COL = '';
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupColumn.length === 1) {
      this.CHECK_COL = this.lookupColumn[0];
      this.getLookupNext(6, 'SYS_ACCT', this.CHECK_COL);
      this.columnChanged();
    }
  }

  columnChanged(): void {
    this.CHECK_SYS_MAP_ACCT = '';
    if (this.lookupSysMapAcct.length === 1) {
      this.CHECK_SYS_MAP_ACCT = this.lookupSysMapAcct[0];
    }
  }

  // Load Audit Data (action code: f67kgj)
  loadAuditData(): void {
    this.loadingAudit = true;
    this.cdr.detectChanges();

    const baseUrl = this.resolveApiBase();
    if (!baseUrl) {
      this.loadingAudit = false;
      return;
    }

    const params: any = {
      action_code: 'f67kgj',
      tax_year: String(this.getTaxYear() ?? ''),
      local_acct_key: String(this.rowData?.LOCAL_ACCT_KEY ?? ''),
      jcd_key: String(this.getJcdKey() ?? ''),
      sso_id: String(this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '')
    };

    this.localSysService.loadAuditData(baseUrl, params, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
            this.auditData = data.jsonObject;
          } else {
            this.auditData = [];
          }
          this.loadingAudit = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.loadingAudit = false;
          this.auditData = [];
          console.error('Error loading audit data:', error);
          this.cdr.detectChanges();
        }
      );
  }

  // Reset Tax Sensitive to original data (re-fetches from server)
  resetTaxSensLookup(): void {
    const epoch = ++this.loadEpoch;
    this.loadSystemTaxsensitivitydata = [];
    this.templocalaccount = [];
    this.fetchTaxSensitiveData(epoch);
  }

  // Save
  save(): void {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const selectedIndex = this.sysAccDropdownDataTemp.findIndex(
      (item: any) => item.SYS_DROPDOWN === this.sysAcctSelected
    );

    if (selectedIndex === -1) {
      this._snackBar.open('Please select a system account', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // TBBS validation (doc §10.2) — block save if TBBS Acct is invalid
    const TBBS_INVALID_VALUES = [
      '', ' - ',
      '----------Chart Based Accounts---------',
      '----------All Other Accounts---------'
    ];
    const editedRows = this.loadSystemTaxsensitivitydata.filter((r: any) => r.saveFlag);
    for (const editRow of editedRows) {
      const tbbsVal = this.resolveDropdownSaveKey(
        editRow.TAX_STREAM_ACCT_DEFAULT, editRow.tbbsacctdropdown_all
      ) || editRow.TAX_STREAM_ACCT || '';
      if (!tbbsVal || TBBS_INVALID_VALUES.includes(tbbsVal)) {
        this._snackBar.open('Please Select TBBS Acct', 'Close', {
          duration: 4000, panelClass: ['warning-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.cdr.detectChanges();
        return;
      }
    }

    const selectedSysAcct = this.sysAccDropdownDataTemp[selectedIndex];
    const source = this.rowData;

    const localSystemAccounts: any[] = [{
      type: 'E',
      object_id: source?.object_id ?? source?.OBJECT_ID,
      system_acct_key_old: source?.SYS_ACCT_KEY,
      system_acct_key_new: selectedSysAcct.SYS_ACCT_KEY,
      local_acct_key: this.localAccountKey ?? source?.LOCAL_ACCT_KEY,
      map_type: source?.MAP_TYPE,
      group_object_key: source?.GROUP_OBJ_KEY,
      tax_year: source?.TAX_YEAR,
      local_chart_key: source?.LOCAL_CHART_KEY,
      acct_type: this.chartAcctData.acct_type,
      scenario: this.getScenario(),
      local_account: source?.LOCAL_ACCT ?? '',
      system_account: this.sysAcctSelected ?? selectedSysAcct.SYS_DROPDOWN ?? ''
    }];

    const saveTaxSensitiveData: any[] = this.loadSystemTaxsensitivitydata
      .filter((row: any) => row.saveFlag)
      .map((row: any) => this.buildSaveDetail(row));

    const baseUrl = this.resolveApiBase();
    this.localSysService.saveFormDetails(
      baseUrl,
      localSystemAccounts,
      saveTaxSensitiveData,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response && response.callSuccess === '0' && response.errorMessage && response.errorMessage !== 'null') {
          let errorMsg = response.errorMessage;
          if (errorMsg.includes('Invalid System Account Number')) {
            errorMsg += ' System Account Key: ' + selectedSysAcct.SYS_ACCT_KEY;
          }
          this._snackBar.open(errorMsg, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else if (response && response.callSuccess === '1') {
          this._snackBar.open('Data has been successfully saved', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open('Local to System mapping has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.setupFormData();
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
